import { useEffect, useRef, type RefObject } from 'react';

export type PinchZoomOptions = {
  enabled: boolean;
  /** Viewport DOM’a bağlandığında effect’i yeniden çalıştır (koşullu render) */
  mountKey?: string | number | boolean;
  minZoom: number;
  maxZoom: number;
  getZoom: () => number;
  setZoom: (zoom: number) => void;
  getPan: () => { x: number; y: number };
  setPan: (pan: { x: number; y: number }) => void;
  /** Pinch başladığında tek parmak pan vb. iptal */
  onPinchStart?: () => void;
};

type PinchState = {
  initialDistance: number;
  initialZoom: number;
  initialPan: { x: number; y: number };
  focalX: number;
  focalY: number;
};

function touchDistance(a: Touch, b: Touch): number {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

function touchCenter(a: Touch, b: Touch): { x: number; y: number } {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

/** iOS Safari: pinch için passive:false touch listener (React synthetic yeterli değil) */
export function usePinchZoom(
  elementRef: RefObject<HTMLElement | null>,
  options: PinchZoomOptions,
) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const pinchRef = useRef<PinchState | null>(null);
  const isPinchingRef = useRef(false);
  const { enabled, mountKey } = options;

  useEffect(() => {
    const el = elementRef.current;
    if (!el || !enabled) return;

    const focalFromViewport = (target: HTMLElement, clientX: number, clientY: number) => {
      const rect = target.getBoundingClientRect();
      return {
        x: clientX - rect.left - rect.width / 2,
        y: clientY - rect.top - rect.height / 2,
      };
    };

    const onTouchStart = (e: TouchEvent) => {
      const opts = optionsRef.current;
      if (!opts.enabled || e.touches.length !== 2) return;

      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const dist = touchDistance(t0, t1);
      if (dist < 8) return;

      const center = touchCenter(t0, t1);
      const focal = focalFromViewport(el, center.x, center.y);

      pinchRef.current = {
        initialDistance: dist,
        initialZoom: opts.getZoom(),
        initialPan: { ...opts.getPan() },
        focalX: focal.x,
        focalY: focal.y,
      };
      isPinchingRef.current = true;
      opts.onPinchStart?.();
      e.preventDefault();
    };

    const onTouchMove = (e: TouchEvent) => {
      const opts = optionsRef.current;
      const pinch = pinchRef.current;
      if (!opts.enabled || !pinch || e.touches.length !== 2) return;

      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const dist = touchDistance(t0, t1);
      if (pinch.initialDistance < 8) return;

      e.preventDefault();

      const scale = dist / pinch.initialDistance;
      const nextZoom = Math.max(
        opts.minZoom,
        Math.min(opts.maxZoom, pinch.initialZoom * scale),
      );
      const ratio = nextZoom / pinch.initialZoom;

      opts.setZoom(nextZoom);
      opts.setPan({
        x: pinch.initialPan.x + pinch.focalX * (1 - ratio),
        y: pinch.initialPan.y + pinch.focalY * (1 - ratio),
      });
    };

    const endPinch = (e: TouchEvent) => {
      if (e.touches.length >= 2) return;
      pinchRef.current = null;
      isPinchingRef.current = false;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', endPinch);
    el.addEventListener('touchcancel', endPinch);

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', endPinch);
      el.removeEventListener('touchcancel', endPinch);
    };
  }, [elementRef, enabled, mountKey]);

  return { isPinchingRef };
}
